import type {FilledContentRelationshipField, FilledLinkToDocumentField, FilledLinkToWebField, LinkField, PrismicDocument} from '@prismicio/types';
import {LinkType} from '@prismicio/types';
import type {LocationSuggestion, QuerySuggestion, QuerySuggestionResponse, QuerySuggestionV2Response} from '@navbar-unlogged/types/custom';

function isArray(element: unknown): element is unknown[] {
    return Array.isArray(element);
}

function hasProperty<T extends string>(element: unknown, property: T): element is Record<T, unknown> {
    if (element === undefined || element === null) {
        return false;
    }
    return Object.prototype.hasOwnProperty.call(element, property);
}

function isArrayOf<T>(element: unknown, checker: (element: unknown) => element is T): element is T[] {
    if (element === undefined || element === null) {
        return false;
    }
    if (!Array.isArray(element)) {
        return false;
    }

    return element.every(checker);
}

function isFilled<T>(argument: T | undefined | null | ''): argument is T {
    return argument !== undefined && argument !== null && argument !== '';
}

function isPrismicLink(link: any): link is LinkField {
    return hasProperty(link, 'link_type') && link.link_type !== undefined;
}
function isFilledLink(link: LinkField): link is FilledLinkToDocumentField<string, string, never> | FilledLinkToWebField {
    return link.link_type !== LinkType.Any && hasProperty(link, 'url') && link.url !== null;
}

function isFilledWebLink(link: LinkField): link is FilledLinkToWebField {
    return link.link_type === LinkType.Web && isFilledLink(link);
}

function isFilledContentRelationshipField(link: LinkField): link is FilledContentRelationshipField {
    return link.link_type === LinkType.Document && 'id' in link && 'lang' in link;
}

function isHTMLElement(element: unknown): element is HTMLElement {
    return (
        element !== null &&
        element !== undefined &&
        (element instanceof HTMLElement || (element instanceof Node && element.nodeType === Node.ELEMENT_NODE))
    );
}

function isHTMLInputElement(target: EventTarget | null | undefined): target is HTMLInputElement {
    return target !== null && target !== undefined && target instanceof HTMLInputElement;
}

function isLocationSuggestion(element: unknown): element is LocationSuggestion {
    return hasProperty(element, 'formattedAddress');
}

function isLocationSuggestionResponse(response: unknown): response is LocationSuggestion[] {
    return isArrayOf(response, isLocationSuggestion);
}

function isLocationSuggestionRecord(record: any): record is Record<string, LocationSuggestion[]> {
    return (
        record !== null &&
        record !== undefined &&
        typeof record === 'object' &&
        Object.keys(record).every((key) => typeof key === 'string') &&
        Object.values(record).every(isLocationSuggestionResponse)
    );
}

function isFilledKeyText(field: any): field is string {
    return typeof field === 'string' && field !== '';
}

function isPrismicDocument(document: any): document is PrismicDocument {
    return (
        document.uid !== undefined &&
        document.first_publication_date !== null &&
        document.last_publication_date !== null &&
        hasProperty(document, 'linked_documents') &&
        Array.isArray(document.linked_documents)
    );
}

export function useTypeGuards() {
    return {
        isFilledKeyText,
        hasProperty,
        isArray,
        isFilled,
        isHTMLInputElement,
        isLocationSuggestionRecord,
        isLocationSuggestionResponse,
        isFilledLink,
        isPrismicLink,
        isFilledWebLink,
        isHTMLElement,
        isPrismicDocument,
        isFilledContentRelationshipField,
    };
}
